import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import type {
  InsertChildIndicatorInput,
  InsertIndicatorMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

export type InsertIndicatorTRPCInput = Omit<
  InsertChildIndicatorInput,
  'schedule' | 'Title' | 'Type' | 'ParentId'
> &
  ScheduleTRPCInput & {
    Title: string;
    Type: NonNullable<InsertChildIndicatorInput['Type']>;
    ParentId: string;
  };

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertIndicatorMutation => {
  return {
    insertChildIndicator: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertIndicatorTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.indicator.insert.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.indicator.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.indicator.indicatorsByParentId.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.control.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.register.queryKey(),
          }),
        ]);
      },
    }),
    throwOnError: true,
  });

  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [mutation.error, addNotification, trpcEnabled]);

  return {
    insertIndicator: async (
      variables: InsertIndicatorTRPCInput
    ): Promise<InsertIndicatorMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
